import React from 'react'
import { debounce } from 'utils/function'
import * as R from 'ramda'

export const useAsyncSelect = (fetchHandler, matcher, transformData) => {
  const [offset] = React.useState(0)
  const [isFetching, setIsFetching] = React.useState(true)

  const loadOptions = React.useCallback(
    async (search, callback) => {
      setIsFetching(true)

      try {
        const { response = {} } = await fetchHandler({
          limit: 50,
          offset,
          search,
        })

        const results = response?.results ?? []

        callback(
          R.map(matcher, transformData ? transformData(results) : results),
        )
      } catch (error) {
        console.error(error)
      } finally {
        setIsFetching(false)
      }
    },
    [matcher, offset, fetchHandler],
  )

  // `loadOptions` is rebuilt whenever its dependencies change, and several
  // callers pass a matcher defined inline in their render, so it changes on
  // every render. Debouncing it directly - as this hook used to - produced a
  // fresh debounced function each time and discarded the pending timer with
  // it, so the search was never actually debounced. Holding the latest
  // callback in a ref keeps the debounced function itself stable for the life
  // of the component, which is what makes the delay work.
  const latestLoadOptions = React.useRef(loadOptions)
  React.useEffect(() => {
    latestLoadOptions.current = loadOptions
  }, [loadOptions])

  const debouncedLoadOptions = React.useMemo(
    () =>
      debounce(
        (search: string, callback: (options: unknown[]) => void) =>
          latestLoadOptions.current(search, callback),
        500,
      ),
    [],
  )

  React.useEffect(() => debouncedLoadOptions.cancel, [debouncedLoadOptions])

  return {
    state: { isFetching },
    handlers: {
      loadOptions: debouncedLoadOptions,
    },
  }
}
